import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daiiqgboh653ivfvoe20";

type Status = "idle" | "sending" | "ok" | "error";

interface SubscribeFormProps {
  variant?: "panel" | "inline";
}

export default function SubscribeForm({ variant = "panel" }: SubscribeFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("ok");
      setMessage("订阅成功，明早的吃瓜日报会准时送到你邮箱。");
      form.reset();
      return;
    }
    payload.delete("website_alt");

    const params = new URLSearchParams();
    payload.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("sending");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("ok");
        setMessage("订阅成功，明早的吃瓜日报会准时送到你邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络好像有点卡，请稍后再试一次。");
    }
  }

  const dark = variant === "panel";

  return (
    <form
      data-readdy-form
      id="subscribe-form"
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-2.5">
        <label className="flex-1">
          <span className="sr-only">邮箱地址</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="填写你的邮箱，明天早上收到今日热帖"
            className={`w-full h-11 px-4 rounded-md text-sm outline-none border transition-colors ${
              dark
                ? "bg-background-50/95 border-transparent text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                : "bg-background-50 border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            }`}
          />
        </label>
        <button
          type="submit"
          disabled={status === "sending"}
          className="h-11 px-6 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-60 transition-colors cursor-pointer"
        >
          {status === "sending" ? "订阅中…" : "订阅吃瓜日报"}
        </button>
      </div>

      {/* anti-spam field */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="gossip-alt-field"
      />

      {message && (
        <p
          role="status"
          className={`mt-3 text-sm ${
            status === "ok"
              ? dark
                ? "text-secondary-200"
                : "text-secondary-700"
              : dark
                ? "text-accent-200"
                : "text-accent-700"
          }`}
        >
          {message}
        </p>
      )}
      <p className={`mt-3 text-xs ${dark ? "text-background-300" : "text-foreground-500"}`}>
        每天一封，只推当日热帖与一句神回复，随时可退。
      </p>
    </form>
  );
}